import React, { useEffect, useState } from "react";
import { Badge, ButtonGroup, Card, Col, Dropdown, DropdownButton, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouteLoaderData } from "react-router-dom";

import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { Awesome, DevModal, OverflowArea } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { Paginator } from "@components/Paginator";
import { buildQS, parseQS, scroll_to_top } from "@components/Utils";
import { getGemHost } from "@vkplay/shared";

export default function ModerateCommentsScreen() {
    return (
        <DefaultLayout>
            <ModerateComments />
        </DefaultLayout>
    );
}

function ModerateComments() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [apiData, setApiData] = useState(undefined);
    const [showModal, setShowModal] = useState(false);
    const { ext_api_get, ext_api_delete } = useRequest();

    const getParams = parseQS();
    if (!["1", "2", "3"].includes(getParams.project)) {
        getParams.project = "2";
    }
    if (!["0", "1"].includes(getParams.hide_smm)) {
        getParams.hide_smm = "1";
    }
    if (!("page" in getParams)) {
        getParams.page = "1";
    }
    const [formData, setFormData] = useState(getParams);

    const commonInfo = useRouteLoaderData("commonInfo");
    const communityHost = getGemHost("community");
    const adminHost = getGemHost("admin").replace("vkplay.ru", "my.games");
    const profileUrl = `https://${adminHost}/admin/?login=`;

    const handleFormChange = (obj) => {
        const newFormData = { ...formData };
        Object.entries(obj).forEach(([k, v]) => {
            newFormData[k] = v;
        });
        setFormData(newFormData);
    };

    const doSearch = (p) => {
        handleFormChange({ page: p });
        const qs = buildQS({ ...formData, page: p });
        navigate(
            {
                pathname: window.location.path,
                search: "?" + qs,
            },
            { replace: true }
        );
        ext_api_get("search", `/comments/moderate/${formData.project}/comments/?${qs}`, {
            then: (resp) => {
                setApiData(resp);
                scroll_to_top();
            },
        });
    };
    useEffect(() => doSearch(formData.page), []);

    if (!apiData) return;

    const doDelete = (ev) => {
        apiData.results.forEach((item) => {
            if (item.chosen) {
                ext_api_delete(ev, `/comments/moderate/${formData.project}/comments/${item.id}`, {
                    fetch_params: {
                        credentials: "include",
                        body: JSON.stringify({
                            csrfmiddlewaretoken_jwt: window.CSRF_JWT_TOKEN,
                            delete_replies: showModal === "branch",
                        }),
                        headers: {
                            "Content-Type": "application/json",
                        },
                    },
                    then: (resp) => {
                        if (resp.ok) {
                            setShowModal(false);
                            doSearch(formData.page);
                        }
                    },
                    finally: false,
                });
            }
        });
    };

    return (
        <Card>
            <Card.Header>
                {t("Комментарии")}
                <div className="dmg-card-header-tools">
                    <DropdownButton as={ButtonGroup} size="sm" variant="dark" title={t("С выбранными")}>
                        <Dropdown.Item onClick={() => setShowModal("item")}>{t("Удалить")}</Dropdown.Item>
                        <Dropdown.Item onClick={() => setShowModal("branch")}>{t("Удалить ветку")}</Dropdown.Item>
                    </DropdownButton>
                </div>
            </Card.Header>
            <Card.Body>
                <div className="mb-3">
                    <Row>
                        <Col>
                            <FormFieldGroup hideLabel={true} asRow={false}>
                                <DevForm.Select
                                    choices={[
                                        { value: "2", label: "Community" },
                                        { value: "1", label: "PLAYERONE" },
                                        { value: "3", label: "Media VK Play" },
                                    ]}
                                    value={formData.project}
                                    onChange={(data) => handleFormChange({ project: data.value })}
                                />
                            </FormFieldGroup>
                        </Col>
                        <Col>
                            <FormFieldGroup hideLabel={true} asRow={false}>
                                <DevForm.DateRangePicker
                                    value={[
                                        (formData.created_at__gte || "").split(" ")[0],
                                        (formData.created_at__lte || "").split(" ")[0],
                                    ]}
                                    placeholder={t("Дата")}
                                    onChange={(rng) => {
                                        handleFormChange({
                                            created_at__gte: `${rng[0]} 00:00:00`,
                                            created_at__lte: `${rng[1]} 23:59:59`,
                                        });
                                    }}
                                />
                            </FormFieldGroup>
                        </Col>
                        <Col>
                            <FormFieldGroup hideLabel={true} asRow={false}>
                                <DevForm.Checkbox
                                    checked={parseInt(formData.hide_smm)}
                                    label={t("Скрыть smm")}
                                    onChange={(ev) => handleFormChange({ hide_smm: ev.target.checked ? 1 : 0 })}
                                />
                            </FormFieldGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormFieldGroup hideLabel={true} asRow={false}>
                                <DevForm.TextInput
                                    value={formData.search}
                                    placeholder={t("Поиск по комментарию")}
                                    onChange={(ev) => handleFormChange({ search: ev.target.value })}
                                />
                            </FormFieldGroup>
                        </Col>
                        <Col>
                            <FormFieldGroup hideLabel={true} asRow={false}>
                                <DevForm.TextInput
                                    value={formData.author}
                                    placeholder={t("Поиск по пользователю (id,email,slug)")}
                                    onChange={(ev) => handleFormChange({ author: ev.target.value })}
                                />
                            </FormFieldGroup>
                        </Col>
                        <Col>
                            <FormFieldGroup hideLabel={true} asRow={false}>
                                <DevForm.TextInput
                                    value={formData.post_hash}
                                    placeholder={t("Поиск по community hash")}
                                    onChange={(ev) => handleFormChange({ post_hash: ev.target.value })}
                                />
                            </FormFieldGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <DevForm.Button variant="primary" name="search" onClick={() => doSearch(1)}>
                                {t("Поиск")}
                            </DevForm.Button>{" "}
                        </Col>
                    </Row>
                </div>

                {apiData.results?.length > 0 && (
                    <Table hover responsive className="dmg-table">
                        <thead>
                            <tr>
                                <th style={{ width: "20px" }}>
                                    <DevForm.Checkbox
                                        checked={
                                            apiData.results.length === apiData.results.filter((o) => o.chosen).length
                                        }
                                        onChange={(ev) => {
                                            const newApiData = { ...apiData };
                                            newApiData.results.forEach((item) => {
                                                item.chosen = ev.target.checked;
                                            });
                                            setApiData(newApiData);
                                        }}
                                    />
                                </th>
                                <th>{t("Профиль")}</th>
                                {formData.project === 2 && <th>{t("Игра")}</th>}
                                <th>{t("Тред")}</th>
                                <th style={{ width: "450px" }}>{t("Комментарий")}</th>
                                <th>{t("Статус")}</th>
                                <th>{t("Время")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiData.results.map((o, ind) => {
                                const clearUrl = o.thread.split("?")[0];
                                const urlParts = clearUrl.split("/").filter((value) => Object.keys(value).length !== 0);
                                const commentDT = new Date(o.created_at).toLocaleString("en-US");
                                const local_date = new Date(commentDT + " UTC");

                                return (
                                    <tr key={o.id}>
                                        <td>
                                            <DevForm.Checkbox
                                                checked={o.chosen}
                                                onChange={(ev) => {
                                                    const newApiData = { ...apiData };
                                                    newApiData.results[ind].chosen = ev.target.checked;
                                                    setApiData(newApiData);
                                                }}
                                            />
                                        </td>
                                        <td>
                                            {o.author.slug && (
                                                <>
                                                    {(commonInfo.account.is_super || commonInfo.account.moderator) && (
                                                        <a href={profileUrl + o.author.slug}>
                                                            <Awesome type="user-circle-o" color="green" />
                                                        </a>
                                                    )}{" "}
                                                    <a href={o.author.url} target="_blank">
                                                        {o.author.username}
                                                    </a>{" "}
                                                    {o.author.is_banned === true && (
                                                        <Badge variant="danger">заблокирован</Badge>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        {formData.project === 2 && (
                                            <td>
                                                {o.post_info && (
                                                    <a
                                                        href={`https://${communityHost}/community/game/${o.post_info.community_game_slug}/`}
                                                        target="_blank"
                                                    >
                                                        {o.post_info.game_name.ru_RU || o.post_info.game_name.en_US}
                                                    </a>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            <a href={formData.project === 2 ? o.thread : clearUrl} target="_blank">
                                                {o.post_info?.post_title || urlParts.pop()}
                                            </a>
                                        </td>
                                        <td>
                                            <OverflowArea y={150}>{o.message}</OverflowArea>
                                        </td>
                                        <td>
                                            <StatusBadge state={o.status} />
                                        </td>
                                        <td>{local_date.toLocaleString()}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
            <Paginator curPage={formData.page} numPages={apiData.total_pages} changePage={doSearch} />

            <DevModal
                show={showModal}
                onHide={() => setShowModal(false)}
                body={t("Подтвердите удаление")}
                footer={
                    <>
                        <DevForm.Button variant="light" name="close" onClick={() => setShowModal(false)}>
                            {t("Закрыть")}
                        </DevForm.Button>
                        <DevForm.Button variant="danger" name="delete" onClick={doDelete}>
                            {t("Удалить")}
                        </DevForm.Button>
                    </>
                }
            />
        </Card>
    );
}

function StatusBadge({ state }) {
    switch (state) {
        case "banned":
            return <Badge variant="warning">Удалено антиспамом</Badge>;
        case "del_u":
            return <Badge variant="secondary">Удалено пользователем</Badge>;
        case "del_m":
            return <Badge variant="danger">Удалено модератором</Badge>;
        case "ok":
            return <Badge variant="success">Опубликовано</Badge>;
        default:
            return null;
    }
}
